import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import {
  HealthCheckSeverityLabel,
  resolveHealthCheckScorePresentation,
} from '../Onboarding/ContentInventory/Components/HealthCheckScorePresentation';
import { liveTermStatusRank } from '../Onboarding/ContentInventory/Components/taxonomyTermIndex';

/** One kind of tag the screen breaks its figures down by, with its selectable tags, one per key. */
export type ContentHealthFacetConfig<TFacet extends TaxonomyFacet = TaxonomyFacet> = {
  facet: TFacet;
  taxonomyKey: string;
  taxonomyName: string;
  terms: { id: string; key: string; name: string }[];
};

/** The most intervals the page-count axis is divided into. */
const MAX_COUNT_AXIS_INTERVALS = 5;

/** The leading digits a count-axis step may have: 1, 2 and 5 times a power of ten. */
const NICE_STEP_FACTORS = [1, 2, 5] as const;

/**
 * The page-count axis of a chart: whole numbers only, in steps of 1, 2 or 5 times a power of ten, so
 * a count of three pages never reads as "1.5".
 */
export function integerCountAxis(maxValue: number): { max: number; ticks: number[] } {
  const ceiling = Math.max(1, Math.ceil(Number.isFinite(maxValue) ? maxValue : 1));

  let step = 1;
  for (let magnitude = 1; ; magnitude *= 10) {
    const fitting = NICE_STEP_FACTORS.map((factor) => factor * magnitude).find(
      (candidate) => Math.ceil(ceiling / candidate) <= MAX_COUNT_AXIS_INTERVALS
    );
    if (fitting !== undefined) {
      step = fitting;
      break;
    }
  }

  const max = step * Math.ceil(ceiling / step);
  const ticks: number[] = [];
  for (let tick = 0; tick <= max; tick += step) {
    ticks.push(tick);
  }

  return { max, ticks };
}

/**
 * A week named by the day it starts on, for example "21 Sep", read from the start Sync Core stamps on
 * the weekly bucket: the Monday of its ISO week. The label takes that day from the bucket itself, so
 * it names whichever day Sync Core starts a week on. Sync Core counts in UTC because the history
 * request names no time zone, so the day is read in UTC too: the same date in every reader's zone.
 */
export function weekStartLabel(bucketStart: number): string {
  const start = new Date(bucketStart);
  // The three-letter month on every engine; some spell September "Sept" in British English.
  const month = start.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${start.getUTCDate()} ${month}`;
}

/**
 * One entry per tag key. The term list can carry a key more than once when the tag has revisions at
 * several statuses; the live revision is kept, in the position the key first appeared at. Sync Core
 * rejects a coverage request that names a key twice.
 */
export function uniqueTermsByKey<TTerm extends { key: string; status?: TaxonomyTermStatus }>(terms: readonly TTerm[]): TTerm[] {
  const byKey = new Map<string, TTerm>();
  for (const term of terms) {
    const existing = byKey.get(term.key);
    if (!existing || liveTermStatusRank(term.status) < liveTermStatusRank(existing.status)) {
      byKey.set(term.key, term);
    }
  }

  return Array.from(byKey.values());
}

/** A 0–1 score as a whole percentage, or nothing when there is no score. */
export function toScorePercentage(score?: number | null): number | undefined {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return undefined;
  }

  return Math.round(Math.max(0, Math.min(1, score)) * 100);
}

/**
 * A 0–1 score as an unrounded percentage, or nothing when there is no score: what a change of health between two
 * shown figures is measured in.
 */
export function toExactScorePercentage(score?: number | null): number | undefined {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return undefined;
  }

  return Math.max(0, Math.min(1, score)) * 100;
}

/**
 * Whether a page, or one check of it, shows the same health in two versions: the same whole percentage, or no figure
 * in either. Health is shown in whole percentages everywhere, so only a health whose shown figure changes is a change.
 */
export function isSameHealth({ current, suggested }: { current?: number | null; suggested?: number | null }): boolean {
  return toScorePercentage(current) === toScorePercentage(suggested);
}

/** The severity band a 0–1 score falls in, the one the check badges use. */
export function healthBandOf(score: number): HealthCheckSeverityLabel {
  return resolveHealthCheckScorePresentation(toScorePercentage(score) ?? 0).label;
}

/** Whether a 0–1 score is in the best band, which is what "passes" means on this screen. */
export function isPassingScore(score: number): boolean {
  return healthBandOf(score) === 'Low';
}

/** Orders by a score, lowest first; a row without one goes last. */
export function compareScoresAscending(left?: number, right?: number): number {
  if (left === undefined && right === undefined) {
    return 0;
  }
  if (left === undefined) {
    return 1;
  }
  if (right === undefined) {
    return -1;
  }

  return left - right;
}

/**
 * The health bands as whole-percentage ranges, lowest first — read off the band function itself so
 * a legend can never disagree with the colours it explains.
 */
export function healthBandRanges(): { band: HealthCheckSeverityLabel; from: number; to: number }[] {
  const ranges: { band: HealthCheckSeverityLabel; from: number; to: number }[] = [];
  for (let percentage = 0; percentage <= 100; percentage++) {
    const band = resolveHealthCheckScorePresentation(percentage).label;
    const last = ranges[ranges.length - 1];
    if (last && last.band === band) {
      last.to = percentage;
    } else {
      ranges.push({ band, from: percentage, to: percentage });
    }
  }

  return ranges;
}
